import React, { useContext, useEffect, useState } from "react";

//** MUI Components */
import { Box, Fade, Stack, Typography } from "@mui/material";

//** NDBX */
import {
  Button,
  ButtonStack,
  NotificationCard,
  OneColumnStack,
  Section,
  TitleSubTitle,
} from "@arp/react-ndbx";

import { useOutletContext } from "react-router-dom";

//** Context  */
import { PaymentContext } from "../../context/BraintreeContext.jsx";
import usePreQualification from "../../hooks/usePreQualification.ts";
import usePreQualificationPages from "../../hooks/usePreQualificationPages.ts";
import useNewBusiness from "../../hooks/useNewBusiness.ts";
import useSharedData from "../../hooks/useSharedData.ts";

//** ARP Components */
import Page from "../../components/Page";
import { LabelTooltip } from "../../components/coding/labelTooltip";
import AccordionRadioButton from "../../components/pageLevel/paymentPaymentDetails/AccordionRadioButton.jsx";
import PaymentDetailsHeader from "../../components/pageLevel/paymentPaymentDetails/PaymentDetailsHeader.jsx";
import SendEmailModal from "../../components/pageLevel/sendEmailModal/SendEmailModal.jsx";

//** Configuration */
import {
  TRACKIFY_EVENT,
  sendEmailModalContent,
} from "../../config/const/enums.js";
import { paymentDetailsDisclaimers } from "../../config/disclaimers/disclaimers.jsx";
import { paymentContactDetailsNotification } from "../../config/notifications/pageNotifications";
import { paymentContactTooltips } from "../../config/tooltips/pageTooltips";
import { retrieveQuoteNotification } from "../../config/disclaimers/disclaimers.jsx";

//* Utilities
import { formatCurrency } from "../../utilities/numericFormat.js";
import FadeInOut from "../../utilities/fadeInOut.jsx";
import { trackifyPageChange } from "@allianz/tracking";

// ----------------------------------------------------------------
export default function PaymentDetailsCC() {
  const { state } = usePreQualificationPages();
  const { currentPage } = { ...state };

  const {
    shouldDisableDoublePayment: [shouldDisableDoublePayment],
    showSecond: [showSecond],
    isDirty: [isDirty],
    isValid: [isValid],
    errors: [errors],
  } = useOutletContext();

  const { dataSet, handleInputChange } = usePreQualification();
  const { retrieveQuoteJourney } = useSharedData();

  const paymentContext = useContext(PaymentContext);
  const {
    isFormValid,
    paymentMethod,
    isGatewayError,
    paymentGatewayError,
    setPaymentFrequencyData,
    setIsContactCentreData,
  } = paymentContext;

  useEffect(() => {
    setIsContactCentreData(true);
  }, []);

  const { data } = useNewBusiness();
  const { quoteInformation } = { ...data };
  const { policyPremiumsTotal } = { ...quoteInformation };

  /* #region Send Email */
  const [openSendEmailModal, setOpenSendEmailModal] = useState(false);
  const handleSendEmailOpen = () => {
    trackifyPageChange(TRACKIFY_EVENT.CC_SEND_EMAIL_PAYMENT);
    setOpenSendEmailModal(true);
  };
  const handleSendEmailClose = (event, reason) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      setOpenSendEmailModal(false);
      setSendQuoteEmailSuccess(false);
    }
  };
  const [sendQuoteEmailSuccess, setSendQuoteEmailSuccess] = useState(false);
  const handleSuccessEmailModal = () => {
    setSendQuoteEmailSuccess(true);
  };
  /* #endregion */

  const [frequency, setFrequency] = useState("monthly");
  const handleOnChange = (e) => {
    if (!e) return;
    setFrequency(e);
    setPaymentFrequencyData(e === "monthly" ? "M" : "A");
    handleInputChange({
      name: "paymentFrequencyUsed",
      value: e,
    });
  };

  const [showNotification, setShowNotification] = useState(false);
  const [showPaymentGatewayError, setShowPaymentGatewayError] = useState(null);
  const fadeTimer = 500;

  useEffect(() => {
    setShowNotification(isGatewayError);
    setShowPaymentGatewayError(paymentGatewayError);
  }, [isGatewayError, paymentGatewayError]);

  //** Page Validation */
  const [shouldDisableButton, setShouldDisableButton] = useState(true);

  useEffect(() => {
    //* The page validation is a little bit different on this page
    //* because it is a mix between yup validation (for the bank details)
    //* and Javascript validation for the iFrame credit card.

    //* First thing, as the user switches between the payment methods,
    //* reset the validation
    if (paymentMethod == null) {
      setShouldDisableButton(true);
    }

    setShouldDisableButton(true);

    if (paymentMethod === "CC" && isFormValid === true) {
      setShouldDisableButton(false);
    }

    if (paymentMethod === "BA" && isValid === true) {
      setShouldDisableButton(false);
    }
  }, [paymentMethod, isFormValid, isValid]);

  useEffect(() => {
    setShowNotification(false);
    setShowPaymentGatewayError(null);
  }, [paymentMethod]);

  useEffect(() => {
    trackifyPageChange(currentPage?.identifier?.toLowerCase());
  }, []);

  //* A callback from the child to determine if there
  //* were any bsb errors (less than 6 chatacters, invalid bsb etc.)
  const [isBsbError, setIsBsbError] = useState(false);

  function onBsbError(value) {
    setIsBsbError(value);
  }
  //** Page Validation */
  //----------------------------------------------------------------

  return (
    <>
      <Section mb={6}>
        <Typography variant="h2-bold">Payment details CC</Typography>
      </Section>
      {dataSet ? (
        <Section sx={{ minWidth: `calc(100vw -  50px)` }}>
          <PaymentDetailsHeader onChange={(e) => handleOnChange(e)} />
        </Section>
      ) : null}

      <Page
        sx={{ outline: "0px solid green" }}
        title={currentPage.title}
        identifier={currentPage.identifier}
      >
        {retrieveQuoteJourney && (
          <Section md={7} mb={4}>
            <FadeInOut show={1 === 1} duration={100} style={{ width: "100%" }}>
              <Box>
                <NotificationCard
                  cardType="info"
                  title="Application retrieval important information"
                  message={retrieveQuoteNotification().DISCLAIMER_TEXT_REVIEW_CC(
                    dataSet["personOne"].emailAddress,
                    dataSet["personOne"].contactNumber
                  )}
                />
              </Box>
            </FadeInOut>
          </Section>
        )}
        {data ? (
          <>
            <OneColumnStack sx={{ width: "max-content" }} mb={6}>
              <Stack direction={"column"} spacing={2}>
                <Typography variant="h3-bold">Select payment method</Typography>
                <LabelTooltip
                  labelHeading={`Note: Account in an individual name (not a Superannuation
                      Fund)`}
                  headingVariant="p3"
                  stackWidth="max-content"
                  controlIdentifier={`select-payment-method-tooltip`}
                  tooltipTitle={
                    paymentContactTooltips.SMSF_PAYMENT_METHOD_TOOLTIP
                  }
                />
              </Stack>
            </OneColumnStack>
            <OneColumnStack
              name="payment-accordion"
              sx={{ maxWidth: { xs: 636 } }}
            >
              {/* This is where we have the credit card and bank account forms */}
              <AccordionRadioButton
                isContactCenter={true}
                onBsbChange={(e) => onBsbError(e)}
              />
            </OneColumnStack>
            {showNotification ? (
              <>
                <OneColumnStack
                  sx={{ maxWidth: { xs: 636 }, width: "100%" }}
                  mt={4}
                >
                  <Fade in={showNotification === true} timeout={fadeTimer}>
                    <Box sx={{ width: "100%" }}>
                      <NotificationCard
                        cardType={
                          paymentContactDetailsNotification
                            ?.PAYMENT_GATEWAY_ERROR?.cardType
                        }
                        title={
                          paymentContactDetailsNotification
                            ?.PAYMENT_GATEWAY_ERROR?.title
                        }
                        message={
                          showPaymentGatewayError === null
                            ? paymentContactDetailsNotification
                                ?.PAYMENT_GATEWAY_ERROR?.message
                            : showPaymentGatewayError
                        }
                        sx={{ minWidth: "100%" }}
                      />
                    </Box>
                  </Fade>
                </OneColumnStack>
              </>
            ) : null}
            <OneColumnStack sx={{ maxWidth: 636 }} mt={4} mb={4}>
              <Box
                sx={{ textAlign: "left" }}
                mb={2}
                id="optional_cover_disclaimer"
              >
                <Typography variant="p2-bold">
                  {
                    paymentDetailsDisclaimers({
                      isOnline: false,
                      frequency: frequency,
                      paymentAmount: formatCurrency(
                        policyPremiumsTotal[frequency]
                      ),
                      paymentMethod:
                        paymentMethod === "CC" ? "credit card" : "bank",
                    }).paymentDetailInfo[0].PAYMENT_AMOUNT.note[0].TITLE
                  }
                </Typography>
                <Typography variant="p2">
                  {
                    paymentDetailsDisclaimers({
                      isOnline: false,
                      frequency: frequency,
                      paymentAmount: formatCurrency(
                        policyPremiumsTotal[frequency]
                      ),
                      paymentMethod:
                        paymentMethod === "CC" ? "credit card" : "bank",
                    }).paymentDetailInfo[0].PAYMENT_AMOUNT.note[0].MESSAGE
                  }
                </Typography>
              </Box>
            </OneColumnStack>
            <OneColumnStack sx={{ maxWidth: 636 }} mt={4} mb={4}>
              <TitleSubTitle
                title={
                  paymentDetailsDisclaimers({
                    isOnline: false,
                    frequency: frequency,
                    paymentAmount: formatCurrency(
                      policyPremiumsTotal[frequency]
                    ),
                    paymentMethod:
                      paymentMethod === "CC" ? "credit card" : "bank",
                  }).paymentDetailInfo[0].IMPORTANT_NOTICE.note[0].TITLE
                }
                subtitle={
                  paymentDetailsDisclaimers({
                    isOnline: false,
                    frequency: frequency,
                    paymentAmount: formatCurrency(
                      policyPremiumsTotal[frequency]
                    ),
                    paymentMethod:
                      paymentMethod === "CC" ? "credit card" : "bank",
                  }).paymentDetailInfo[0].IMPORTANT_NOTICE.note[0].MESSAGE
                }
                variant="p2-bold"
              />
            </OneColumnStack>{" "}
          </>
        ) : null}

        <Section>
          <ButtonStack>
            <Button
              variant="contained"
              color="secondary"
              // disabled={shouldDisableButton || shouldDisableDoublePayment}
              onClick={handleSendEmailOpen}
            >
              SAVE AND EMAIL
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={
                shouldDisableButton || shouldDisableDoublePayment || isBsbError
              }
            >
              PROCESS PAYMENT
            </Button>
          </ButtonStack>
        </Section>
        <SendEmailModal
          openSendEmailModal={openSendEmailModal}
          handleSendEmailClose={handleSendEmailClose}
          handleSuccessEmailModal={handleSuccessEmailModal}
          sendQuoteEmailSuccess={sendQuoteEmailSuccess}
          sendEmailImg={sendEmailModalContent.SEND_EMAIL_IMAGE}
        />
      </Page>
    </>
  );
}
